import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Dropdown } from 'react-bootstrap';
import { Bell } from 'lucide-react';
import { getMyNotifications, markNotificationRead, clearAllNotifications } from '../api/optimalimsapi';

const NotificationDropdown = ({ isAppTopbar = false, notifications: propNotifications }) => {
    const navigate = useNavigate();
    const dropdownRef = useRef(null);
    const autoPopupTimerRef = useRef(null);

    const { user } = useSelector((state) => ({
        user: state.Auth?.user,
    }));

    const [isOpen, setIsOpen] = useState(false);
    const [notificationsList, setNotificationsList] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);

    // Auto popup states for admin (and lab managers) on login
    const [autoPopupNotif, setAutoPopupNotif] = useState(null);
    const [showAutoPopup, setShowAutoPopup] = useState(false);

    // Fetch user-scoped notifications
    const fetchNotifications = async () => {
        if (!user || !user._id) return;
        try {
            setLoading(true);
            const res = await getMyNotifications({
                userId: user._id,
                role: user.role,
                department: user.department,
            });

            if (res && Array.isArray(res.notifications)) {
                setNotificationsList(res.notifications);
                setUnreadCount(res.unreadCount || 0);
            } else if (Array.isArray(res)) {
                setNotificationsList(res);
                const unread = res.filter((n) => !n.isRead).length;
                setUnreadCount(unread);
            }
        } catch (err) {
            console.error('Error fetching notifications:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (propNotifications && Array.isArray(propNotifications)) {
            setNotificationsList(propNotifications);
            const unread = propNotifications.filter((n) => !n.isRead).length;
            setUnreadCount(unread);
        } else {
            fetchNotifications();
        }
    }, [user?._id, user?.role, propNotifications]);

    // Handle auto-popup for 3 seconds on login
    useEffect(() => {
        if (!notificationsList || notificationsList.length === 0 || !user?._id) return;

        // Find the latest due date warning notification
        const dueNotif = notificationsList.find(
            (n) => n.type === 'DUE_DATE_WARNING' && n.counts && (n.counts.alreadyExceededCount > 0 || n.counts.dueTodayCount > 0)
        );

        if (dueNotif) {
            const sessionKey = `lims_due_popup_seen_${user._id}_${dueNotif._id || dueNotif.dateKey}`;
            const alreadySeen = sessionStorage.getItem(sessionKey);

            if (!alreadySeen) {
                sessionStorage.setItem(sessionKey, 'true');
                setAutoPopupNotif(dueNotif);
                setShowAutoPopup(true);

                if (autoPopupTimerRef.current) {
                    clearTimeout(autoPopupTimerRef.current);
                }
                autoPopupTimerRef.current = setTimeout(() => {
                    setShowAutoPopup(false);
                }, 5000);
            }
        }
    }, [notificationsList, user?._id]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (autoPopupTimerRef.current) {
                clearTimeout(autoPopupTimerRef.current);
            }
        };
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleToggle = () => {
        if (showAutoPopup) {
            setShowAutoPopup(false);
            if (autoPopupTimerRef.current) clearTimeout(autoPopupTimerRef.current);
        }
        const nextState = !isOpen;
        setIsOpen(nextState);
        if (nextState && (!propNotifications || propNotifications.length === 0)) {
            fetchNotifications();
        }
    };

    const handleMarkAsRead = async (id, e) => {
        if (e) e.stopPropagation();
        try {
            await markNotificationRead({ id, userId: user?._id });
            setNotificationsList((prev) =>
                prev.map((item) => (item._id === id ? { ...item, isRead: true } : item))
            );
            setUnreadCount((prev) => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Failed to mark read:', err);
        }
    };

    const handleClearAll = async (e) => {
        if (e) e.preventDefault();
        try {
            await clearAllNotifications({ userId: user?._id });
            setNotificationsList([]);
            setUnreadCount(0);
            setIsOpen(false);
        } catch (err) {
            console.error('Failed to clear notifications:', err);
        }
    };

    const handleCardClick = async (notif) => {
        if (!notif.isRead) {
            await handleMarkAsRead(notif._id);
        }
        setIsOpen(false);
        const targetLink = notif.link || '/lims/Sample?status=Due Date Exceeded';
        navigate(targetLink);
    };

    const handleFilterClick = async (notif, status, e) => {
        if (e) e.stopPropagation();
        if (!notif.isRead) {
            await handleMarkAsRead(notif._id);
        }
        setIsOpen(false);
        navigate(`/lims/Sample?status=${encodeURIComponent(status)}`);
    };

    const formatDateLabel = (notif) => {
        if (!notif.dateKey && !notif.createdAt) return 'Recent';
        const dateStr = notif.dateKey || String(notif.createdAt).slice(0, 10);
        const todayStr = new Date().toISOString().slice(0, 10);
        if (dateStr === todayStr) return 'Today';

        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().slice(0, 10);
        if (dateStr === yesterdayStr) return 'Yesterday';

        return dateStr;
    };

    // Render 3-second auto-popup bubble
    const renderAutoPopup = () => {
        if (!showAutoPopup || !autoPopupNotif || isOpen) return null;

        const counts = autoPopupNotif.counts || {};
        const alreadyExceeded = Number(counts.alreadyExceededCount) || 0;
        const dueToday = Number(counts.dueTodayCount) || 0;

        return (
            <div
                className="lims-notif-auto-popup"
                onClick={() => {
                    setShowAutoPopup(false);
                    if (autoPopupTimerRef.current) clearTimeout(autoPopupTimerRef.current);
                    handleCardClick(autoPopupNotif);
                }}
                title="Click to view overdue samples"
            >
                <div className="d-flex align-items-center justify-content-between mb-1">
                    <span className="fw-bold font-13 text-dark d-flex align-items-center gap-1">
                        <i className="mdi mdi-alert-circle text-danger font-16"></i>
                        {autoPopupNotif.title || 'Due Date Alert'}
                    </span>
                    <span className="badge bg-danger text-white font-10">Attention</span>
                </div>
                <div className="mt-2">
                    <div className="d-flex justify-content-between align-items-center py-1 font-12">
                        <span className="text-secondary d-flex align-items-center gap-1">
                            <i className="mdi mdi-clock-alert-outline text-danger"></i>
                            Already Overdue:
                        </span>
                        <span className="badge rounded-pill bg-danger text-white">
                            {alreadyExceeded}
                        </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-1 font-12">
                        <span className="text-secondary d-flex align-items-center gap-1">
                            <i className="mdi mdi-calendar-clock text-warning"></i>
                            Due Today:
                        </span>
                        <span className="badge rounded-pill bg-warning text-dark">
                            {dueToday}
                        </span>
                    </div>
                </div>
                <div className="lims-notif-auto-popup-bar" />
            </div>
        );
    };

    // Render full dropdown content list
    const renderDropdownContent = () => (
        <>
            <div className="lims-notif-header">
                <h6 className="lims-notif-title">Notifications</h6>
                {notificationsList.length > 0 && (
                    <button
                        type="button"
                        className="lims-notif-clear-btn"
                        onClick={handleClearAll}
                    >
                        Clear All
                    </button>
                )}
            </div>

            <div className="lims-notif-body">
                {loading && notificationsList.length === 0 ? (
                    <div className="text-center py-4 text-muted font-13">
                        Loading notifications...
                    </div>
                ) : notificationsList.length === 0 ? (
                    <div className="text-center py-4 text-muted font-13">
                        <i className="mdi mdi-bell-off-outline font-22 d-block mb-1 text-muted"></i>
                        No new notifications
                    </div>
                ) : (
                    notificationsList.map((notif, index) => {
                        const counts = notif.counts || {};
                        const alreadyExceeded = Number(counts.alreadyExceededCount) || 0;
                        const dueToday = Number(counts.dueTodayCount) || 0;
                        const isWarning = notif.type === 'DUE_DATE_WARNING';
                        const dateLabel = formatDateLabel(notif);

                        return (
                            <div
                                key={notif._id || index}
                                className={`lims-notif-card ${!notif.isRead ? 'unread' : ''} ${
                                    isWarning ? 'lims-notif-card-warning' : ''
                                }`}
                                onClick={() => handleCardClick(notif)}
                            >
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                    <div className="d-flex align-items-center gap-1">
                                        {isWarning && (
                                            <i
                                                className="mdi mdi-alert-circle text-danger"
                                                style={{ fontSize: '15px' }}
                                            ></i>
                                        )}
                                        <span className="fw-semibold font-13 text-dark">
                                            {notif.title || 'Notification'}
                                        </span>
                                    </div>
                                    <span className="badge bg-light text-secondary font-11">
                                        {dateLabel}
                                    </span>
                                </div>

                                {isWarning ? (
                                    <div className="mt-2">
                                        {/* Listing 1: Already due date exceed sample count */}
                                        <div
                                            className="lims-notif-stat-row"
                                            title="Click to view samples whose due date already exceeded"
                                            onClick={(e) =>
                                                handleFilterClick(notif, 'Due Date Exceeded', e)
                                            }
                                        >
                                            <span className="text-muted d-flex align-items-center gap-1">
                                                <i className="mdi mdi-clock-alert-outline text-danger"></i>
                                                Already Due Date Exceeded:
                                            </span>
                                            <span
                                                className={`badge rounded-pill ${
                                                    alreadyExceeded > 0
                                                        ? 'bg-danger text-white'
                                                        : 'bg-light text-muted'
                                                }`}
                                            >
                                                {alreadyExceeded}
                                            </span>
                                        </div>

                                        {/* Listing 2: Todays due date exceeding count */}
                                        <div
                                            className="lims-notif-stat-row mt-1"
                                            title="Click to view samples due today"
                                            onClick={(e) =>
                                                handleFilterClick(notif, 'Due Today', e)
                                            }
                                        >
                                            <span className="text-muted d-flex align-items-center gap-1">
                                                <i className="mdi mdi-calendar-clock text-warning"></i>
                                                Today's Due Date Exceeding:
                                            </span>
                                            <span
                                                className={`badge rounded-pill ${
                                                    dueToday > 0
                                                        ? 'bg-warning text-dark'
                                                        : 'bg-light text-muted'
                                                }`}
                                            >
                                                {dueToday}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="font-12 text-muted mt-1">
                                        {notif.message || notif.subText || ''}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            <div className="lims-notif-footer">
                <Link
                    to="/lims/Sample?status=Due Date Exceeded"
                    onClick={() => setIsOpen(false)}
                >
                    View Overdue Samples →
                </Link>
            </div>
        </>
    );

    // AppTopbar variant (integrated into modern topbar)
    if (isAppTopbar) {
        return (
            <div className="lims-tn-notif-wrapper" ref={dropdownRef}>
                <button
                    className="lims-tn-notif"
                    type="button"
                    aria-label="Notifications"
                    onClick={handleToggle}
                    title="Notifications"
                >
                    <Bell size={14} />
                    {unreadCount > 0 && (
                        <span className="lims-tn-notif-badge">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </button>

                {renderAutoPopup()}

                {isOpen && (
                    <div className="lims-notif-dropdown">
                        {renderDropdownContent()}
                    </div>
                )}
            </div>
        );
    }

    // Classic Bootstrap variant for Topbar.js
    return (
        <Dropdown show={isOpen} onToggle={handleToggle} ref={dropdownRef} className="position-relative">
            <Dropdown.Toggle
                variant="link"
                id="dropdown-notification"
                as={Link}
                to="#"
                onClick={(e) => {
                    e.preventDefault();
                    handleToggle();
                }}
                className="nav-link dropdown-toggle arrow-none"
            >
                <i className="dripicons-bell noti-icon"></i>
                {unreadCount > 0 && <span className="noti-icon-badge"></span>}
            </Dropdown.Toggle>

            {renderAutoPopup()}

            <Dropdown.Menu align="end" className="dropdown-menu-animated dropdown-lg p-0" style={{ width: '360px' }}>
                {renderDropdownContent()}
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default NotificationDropdown;
